import { useState } from "react";

import { Pencil, Trash2 } from "lucide-react";

import { MAX_COMMENT_LENGTH } from "./frameMeta";

import type { FrameComment } from "./frameMeta";

export type CommentThreadProps = {
  comments: readonly FrameComment[];
  /** Whether this viewer may write a comment here — a teacher always can;
   * a student only on a frame they're allowed to comment on. */
  canWrite: boolean;
  /** Whether this viewer may edit or delete this comment — a teacher any,
   * a student only their own. */
  canManage(comment: FrameComment): boolean;
  onAdd(text: string): void;
  onEdit(commentId: string, text: string): void;
  onRemove(commentId: string): void;
};

/**
 * A frame's comment thread: a scrollable bubble list with a write form
 * pinned under it, kakao-chat-shaped but drawn with canvaschool's own
 * tokens. Lives inside `CommentModal` now — it used to be the whole of
 * `CommentPane`, docked under the frame on the canvas itself.
 *
 * Editing happens in the bubble itself; deleting asks first through a
 * confirm drawn over the thread rather than a second `Modal`, whose own
 * Escape listener would close the comment modal along with it.
 */
export const CommentThread = ({
  comments,
  canWrite,
  canManage,
  onAdd,
  onEdit,
  onRemove,
}: CommentThreadProps) => {
  const [draft, setDraft] = useState("");
  const [editing, setEditing] = useState<{ id: string; text: string } | null>(
    null,
  );
  const [removingId, setRemovingId] = useState<string | null>(null);

  const saveEdit = () => {
    if (!editing) {
      return;
    }
    const text = editing.text.trim();
    const original = comments.find((comment) => comment.id === editing.id);
    if (text !== "" && original && text !== original.text) {
      onEdit(editing.id, text);
    }
    setEditing(null);
  };

  return (
    <div className="canvaschool-comment-thread">
      <ul className="canvaschool-comment-list">
        {comments.length === 0 && (
          <li className="canvaschool-comment-empty">아직 댓글이 없어요.</li>
        )}
        {comments.map((comment) => {
          const isEditing = editing?.id === comment.id;
          return (
            <li
              key={comment.id}
              className={
                comment.authorRole === "TEACHER"
                  ? "canvaschool-bubble canvaschool-bubble--teacher"
                  : "canvaschool-bubble"
              }
            >
              <span className="canvaschool-bubble-head">
                <span className="canvaschool-bubble-author">
                  {comment.authorLabel}
                  {comment.editedAt && (
                    <span className="canvaschool-bubble-edited"> · 수정됨</span>
                  )}
                </span>
                {!isEditing && canManage(comment) && (
                  <span className="canvaschool-bubble-actions">
                    <button
                      type="button"
                      className="canvaschool-bubble-action"
                      aria-label="댓글 수정"
                      title="댓글 수정"
                      onClick={() =>
                        setEditing({ id: comment.id, text: comment.text })
                      }
                    >
                      <Pencil size={12} aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      className="canvaschool-bubble-action canvaschool-bubble-action--danger"
                      aria-label="댓글 삭제"
                      title="댓글 삭제"
                      onClick={() => setRemovingId(comment.id)}
                    >
                      <Trash2 size={12} aria-hidden="true" />
                    </button>
                  </span>
                )}
              </span>
              {isEditing ? (
                <form
                  className="canvaschool-bubble-edit"
                  onSubmit={(event) => {
                    event.preventDefault();
                    saveEdit();
                  }}
                >
                  <textarea
                    aria-label="댓글 고치기"
                    autoFocus
                    rows={2}
                    maxLength={MAX_COMMENT_LENGTH}
                    value={editing.text}
                    onChange={(event) =>
                      setEditing({ id: comment.id, text: event.target.value })
                    }
                    onKeyDown={(event) => {
                      if (event.key === "Escape") {
                        // Cancel just the edit — not the whole comment modal.
                        event.nativeEvent.stopPropagation();
                        setEditing(null);
                      } else if (
                        event.key === "Enter" &&
                        !event.shiftKey &&
                        !event.nativeEvent.isComposing
                      ) {
                        event.preventDefault();
                        saveEdit();
                      }
                    }}
                  />
                  <span className="canvaschool-bubble-edit-actions">
                    <button type="button" onClick={() => setEditing(null)}>
                      취소
                    </button>
                    <button type="submit" disabled={editing.text.trim() === ""}>
                      저장
                    </button>
                  </span>
                </form>
              ) : (
                <span className="canvaschool-bubble-text">{comment.text}</span>
              )}
            </li>
          );
        })}
      </ul>
      {canWrite && (
        <form
          className="canvaschool-comment-form"
          onSubmit={(event) => {
            event.preventDefault();
            const text = draft.trim();
            if (text !== "") {
              onAdd(text);
              setDraft("");
            }
          }}
        >
          <input
            type="text"
            aria-label="댓글 쓰기"
            placeholder="댓글을 입력하세요"
            maxLength={MAX_COMMENT_LENGTH}
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
          />
          <button
            type="submit"
            className="canvaschool-btn-primary"
            disabled={draft.trim() === ""}
          >
            쓰기
          </button>
        </form>
      )}
      {removingId && (
        <div
          className="canvaschool-comment-confirm"
          role="alertdialog"
          aria-modal="true"
          aria-label="댓글 삭제 확인"
          onKeyDown={(event) => {
            if (event.key === "Escape") {
              // Close just this confirm — not the whole comment modal.
              event.nativeEvent.stopPropagation();
              setRemovingId(null);
            }
          }}
        >
          <div className="canvaschool-comment-confirm-box">
            <p>이 댓글을 삭제할까요?</p>
            <div className="canvaschool-modal-actions">
              <button
                type="button"
                className="canvaschool-btn-secondary"
                autoFocus
                onClick={() => setRemovingId(null)}
              >
                취소
              </button>
              <button
                type="button"
                className="canvaschool-btn-danger"
                onClick={() => {
                  onRemove(removingId);
                  setRemovingId(null);
                }}
              >
                삭제
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
